import React, { FormEvent, useEffect, useState } from 'react'
import { ConfirmationInput } from '../../../../../../../shared/components/confirmation-input/ConfirmationInput'
import { ESignupFormSteps } from '../../../../../../../types/enums/ESignupFormSteps'
import {
  ResendButton,
  ResendButtonText,
  ResendButtonWrapper,
} from '../../../../../common/ResendButtonWrapper'
import {
  confirmSignUp,
  resendConfirmSignUp,
  signIn,
} from '../../../../../../../services/AuthService'
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../../../store/store'
import { setToken } from '../../../../../../../store/reducers/userReducer'
import { ProgressBar } from 'shared/components/progress-bar/ProgressBar'
import { useTranslation } from 'react-i18next'
import {
  setAuthFormTexts,
  setForm,
  setIsLayoutLoading,
} from 'store/reducers/layoutReducer'
import { Button } from 'shared/components/button/Button'
import { EPersonalDetailsFormSteps } from 'types/enums/EPersonalDetailsFormSteps'

export const SignupFormConfirmation: React.FC = () => {
  const user = useAppSelector((state) => state.user)
  const layoutEmail = useAppSelector((state) => state.layout.user.email)
  const dispatch = useAppDispatch()
  const [code, setCode] = useState<string>('')
  const [hasError, setHasError] = useState<boolean>(false)
  const [isDisabled, setIsDisabled] = useState<boolean>(true)

  const { t } = useTranslation('', {
    keyPrefix: `pages.signUp.confirmation`,
  })

  useEffect(() => {
    dispatch(
      setAuthFormTexts({
        title: t('email.title'),
        subtitle: `${t('email.subtitle-1')} ${user.email}. ${t(
          'email.subtitle-2',
        )}`,
      }),
    )
  }, [t])

  const handleConfirmationChange = (value: string, error?: boolean) => {
    setCode(value)
    if (error) {
      if (!isDisabled) {
        setIsDisabled(true)
      }
    } else if (isDisabled) {
      setIsDisabled(false)
    }
  }

  const handleConfirmationSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault()
    dispatch(setIsLayoutLoading(true))

    if (!user.password) {
      dispatch(setForm({ formStep: ESignupFormSteps.password }))
      return
    }

    const isConfirmed = await confirmSignUp(user.email || '', code)
    if (!isConfirmed) {
      setHasError(true)
      dispatch(setIsLayoutLoading(false))
      return
    }

    const signedUser = await signIn(user.email || '', user.password)
    if (signedUser) {
      if ('error' in signedUser) {
        // TODO error handling
        return
      }
      dispatch(setToken(signedUser.token))
      dispatch(
        setForm({
          formStep: ESignupFormSteps.personalDetails,
          personalDetailsStep: EPersonalDetailsFormSteps.personalInfo,
        }),
      )
    }
  }

  const handleResendClick = async (event: FormEvent) => {
    event.preventDefault()
    const email = user.email || layoutEmail
    await resendConfirmSignUp(email)
  }

  return (
    <>
      <ConfirmationInput
        hasError={hasError}
        labelText={t('inputs.confirmation.labelText')}
        errorText={t('inputs.confirmation.errorText')}
        callback={handleConfirmationChange}
      ></ConfirmationInput>
      <Button
        type="submit"
        disabled={isDisabled}
        className={`button ${isDisabled ? 'disabled' : ''}`}
        text={t('buttons.next.text')}
        callback={handleConfirmationSubmit}
      />
      <ResendButtonWrapper>
        <ResendButtonText>{t('buttons.resend.title')}</ResendButtonText>
        <ResendButton
          disabled={false}
          className={`resendButton bold`}
          text={t('buttons.resend.text')}
          callback={handleResendClick}
        ></ResendButton>
      </ResendButtonWrapper>
      <ProgressBar progress={30} />
    </>
  )
}
